import React, { useState, useEffect, useRef } from 'react';
import { X, Plus, Edit, Trash2, Save, Search, Database, Globe, Tag, TrendingUp, Clock, Languages, Type as TypeIcon, Image as ImageIcon, Upload, Link as LinkIcon } from 'lucide-react';
import { templateAPI, photoAPI } from '../services/api';

const GlobalFieldSamplesModal = ({ isOpen, onClose, globalField }) => {
  const [samples, setSamples] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLanguage, setSelectedLanguage] = useState('en');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingSample, setEditingSample] = useState(null);
  const [bulkMode, setBulkMode] = useState(false);
  const [bulkText, setBulkText] = useState('');
  const [statistics, setStatistics] = useState(null);
  const [availableLanguages] = useState([
    { code: 'en', name: 'English' },
    { code: 'es', name: 'Spanish' },
    { code: 'fr', name: 'French' },
    { code: 'de', name: 'German' },
    { code: 'it', name: 'Italian' },
    { code: 'pt', name: 'Portuguese' },
    { code: 'ru', name: 'Russian' },
    { code: 'ja', name: 'Japanese' },
    { code: 'ko', name: 'Korean' },
    { code: 'zh', name: 'Chinese' }
  ]);

  const [formData, setFormData] = useState({
    sample_value: '',
    sample_type: 'text',
    language: 'en',
    category: '',
    tags: ''
  });

  // Image sample upload state (used for multiselect-image fields)
  const [uploadingImage, setUploadingImage] = useState(false);
  const fileInputRef = useRef(null);

  // Whether the current field manages images (Text or Image values)
  const isImageField = globalField?.fieldType === 'multiselect-image';
  // For image fields, treat any non-text sample_type as an image entry
  const sampleKind = formData.sample_type === 'text' ? 'text' : 'image';

  const setSampleKind = (kind) => {
    setFormData((prev) => ({
      ...prev,
      sample_type: kind === 'text' ? 'text' : 'image',
      // Clear the value when switching kinds to avoid mixing a URL with plain text
      sample_value: ''
    }));
  };

  const handleImageUpload = async (file) => {
    if (!file) return;
    setUploadingImage(true);
    setError(null);
    try {
      const fd = new FormData();
      fd.append('photo', file);
      fd.append('name', file.name);
      fd.append('category', '');
      fd.append('description', '');
      fd.append('tags', '');
      // Persist the uploaded image so its URL is durable
      fd.append('isPermanent', true);

      const response = await photoAPI.upload(fd);
      const data = response?.data?.data || {};
      const url = data.storageUrl || data.url || data.thumbnailUrl;
      if (!url) {
        throw new Error('Upload succeeded but no URL was returned');
      }
      setFormData((prev) => ({ ...prev, sample_type: 'image', sample_value: url }));
      setSuccess('Image uploaded');
    } catch (err) {
      console.error('Error uploading image sample:', err);
      setError(err.response?.data?.message || err.message || 'Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };

  useEffect(() => {
    if (isOpen && globalField) {
      fetchSamples();
      fetchStatistics();
    }
  }, [isOpen, globalField, selectedLanguage, selectedCategory, searchTerm]);

  const fetchSamples = async () => {
    if (!globalField) return;

    setLoading(true);
    setError(null);

    try {
      const params = {};
      if (selectedLanguage) params.language = selectedLanguage;
      if (selectedCategory) params.category = selectedCategory;
      if (searchTerm) params.search = searchTerm;

      const response = await templateAPI.getGlobalFieldSamples(globalField.id, params);
      setSamples(response.data || []);
    } catch (err) {
      setError('Failed to fetch samples');
      console.error('Error fetching samples:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchStatistics = async () => {
    if (!globalField) return;

    try {
      const response = await templateAPI.getGlobalFieldSampleStatistics({ global_field_id: globalField.id });
      setStatistics(response.data);
    } catch (err) {
      console.error('Error fetching statistics:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!globalField) return;

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const sampleData = {
        ...formData,
        global_field_id: globalField.id
      };

      if (editingSample) {
        await templateAPI.updateGlobalFieldSample(editingSample.id, sampleData);
        setSuccess('Sample updated successfully');
      } else {
        await templateAPI.createGlobalFieldSample(sampleData);
        setSuccess('Sample created successfully');
      }

      resetForm();
      await fetchSamples();
      await fetchStatistics();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save sample');
    } finally {
      setLoading(false);
    }
  };

  const handleBulkCreate = async () => {
    if (!globalField || !bulkText.trim()) return;

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const lines = bulkText.split('\n').filter(line => line.trim());
      const samplesData = lines.map(line => ({
        global_field_id: globalField.id,
        sample_value: line.trim(),
        sample_type: formData.sample_type,
        language: formData.language,
        category: formData.category,
        tags: formData.tags
      }));

      await templateAPI.bulkCreateGlobalFieldSamples({ samples: samplesData });
      setSuccess(`Created ${samplesData.length} samples successfully`);
      
      setBulkText('');
      setBulkMode(false);
      await fetchSamples();
      await fetchStatistics();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create bulk samples');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (sample) => {
    setEditingSample(sample);
    setFormData({
      sample_value: sample.sample_value,
      sample_type: sample.sample_type,
      language: sample.language,
      category: sample.category || '',
      tags: sample.tags || ''
    });
    setShowCreateForm(true);
  };

  const handleDelete = async (sampleId) => {
    if (!window.confirm('Are you sure you want to delete this sample?')) return;

    setLoading(true);
    setError(null);

    try {
      await templateAPI.deleteGlobalFieldSample(sampleId);
      setSuccess('Sample deleted successfully');
      await fetchSamples();
      await fetchStatistics();
    } catch (err) {
      setError('Failed to delete sample');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      sample_value: '',
      // Default new image-field samples to the Image kind, everything else to text
      sample_type: isImageField ? 'image' : 'text',
      language: 'en',
      category: '',
      tags: ''
    });
    setEditingSample(null);
    setShowCreateForm(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  if (!isOpen || !globalField) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center space-x-3">
            <Database className="w-6 h-6 text-green-600" />
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                Manage Samples: {globalField.label}
              </h2>
              <p className="text-sm text-gray-500">
                Field Key: {globalField.key} • Type: {globalField.fieldType}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Statistics */}
        {statistics && (
          <div className="p-4 bg-gray-50 border-b">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">{statistics.total_samples}</div>
                <div className="text-sm text-gray-600">Total Samples</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">{statistics.languages?.length || 0}</div>
                <div className="text-sm text-gray-600">Languages</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-purple-600">{statistics.types?.length || 0}</div>
                <div className="text-sm text-gray-600">Types</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-orange-600">{statistics.most_used?.length || 0}</div>
                <div className="text-sm text-gray-600">Most Used</div>
              </div>
            </div>
          </div>
        )}

        {/* Controls */}
        <div className="p-4 border-b bg-white">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center space-x-4">
              {/* Search */}
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search samples..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {/* Language Filter */}
              <select
                value={selectedLanguage}
                onChange={(e) => setSelectedLanguage(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">All Languages</option>
                {availableLanguages.map(lang => (
                  <option key={lang.code} value={lang.code}>{lang.name}</option>
                ))}
              </select>

              {/* Category Filter */}
              <input
                type="text"
                placeholder="Filter by category..."
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div className="flex items-center space-x-2">
              <button
                onClick={() => setBulkMode(!bulkMode)}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  bulkMode 
                    ? 'bg-purple-600 text-white hover:bg-purple-700' 
                    : 'bg-purple-100 text-purple-700 hover:bg-purple-200'
                }`}
              >
                Bulk Mode
              </button>
              <button
                onClick={() => setShowCreateForm(true)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center space-x-2"
              >
                <Plus className="w-4 h-4" />
                <span>Add Sample</span>
              </button>
            </div>
          </div>

          {/* Bulk Mode */}
          {bulkMode && (
            <div className="mt-4 p-4 bg-purple-50 border border-purple-200 rounded-lg">
              <h3 className="font-medium text-purple-900 mb-2">Bulk Create Samples</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <textarea
                    value={bulkText}
                    onChange={(e) => setBulkText(e.target.value)}
                    placeholder="Enter one sample per line..."
                    rows={6}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Language</label>
                    <select
                      value={formData.language}
                      onChange={(e) => setFormData(prev => ({ ...prev, language: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      {availableLanguages.map(lang => (
                        <option key={lang.code} value={lang.code}>{lang.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                    <input
                      type="text"
                      value={formData.category}
                      onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Optional category"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Tags</label>
                    <input
                      type="text"
                      value={formData.tags}
                      onChange={(e) => setFormData(prev => ({ ...prev, tags: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Comma-separated tags"
                    />
                  </div>
                  <button
                    onClick={handleBulkCreate}
                    disabled={loading || !bulkText.trim()}
                    className="w-full px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                  >
                    {loading ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    ) : (
                      <>
                        <Plus className="w-4 h-4" />
                        <span>Create {bulkText.split('\n').filter(line => line.trim()).length} Samples</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Messages */}
        {error && (
          <div className="mx-4 mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700">
            {error}
          </div>
        )}
        {success && (
          <div className="mx-4 mt-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700">
            {success}
          </div>
        )}

        {/* Samples List */}
        <div className="flex-1 overflow-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            </div>
          ) : samples.length === 0 ? (
            <div className="text-center py-12">
              <Database className="w-12 h-12 mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500">No samples found</p>
              <button
                onClick={() => setShowCreateForm(true)}
                className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Create First Sample
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {samples.map((sample) => (
                <div key={sample.id} className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1 min-w-0">
                      {isImageField && sample.sample_type !== 'text' ? (
                        <div>
                          <img
                            src={sample.sample_value}
                            alt="sample"
                            className="w-full h-28 object-contain rounded-md border border-gray-100 bg-gray-50"
                            onError={(e) => { e.currentTarget.style.display = 'none'; }}
                          />
                          <div className="text-xs text-gray-400 mt-1 break-all truncate" title={sample.sample_value}>
                            {sample.sample_value}
                          </div>
                        </div>
                      ) : (
                        <div className="font-medium text-gray-900 break-words">
                          {sample.sample_value}
                        </div>
                      )}
                      <div className="flex items-center space-x-2 mt-1">
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                          <Globe className="w-3 h-3 mr-1" />
                          {sample.language.toUpperCase()}
                        </span>
                        {sample.category && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                            <Tag className="w-3 h-3 mr-1" />
                            {sample.category}
                          </span>
                        )}
                      </div>
                      {sample.tags && (
                        <div className="text-xs text-gray-500 mt-1">
                          Tags: {sample.tags}
                        </div>
                      )}
                      <div className="flex items-center space-x-4 text-xs text-gray-500 mt-2">
                        <span className="flex items-center">
                          <TrendingUp className="w-3 h-3 mr-1" />
                          Used {sample.usage_count} times
                        </span>
                        {sample.last_used_at && (
                          <span className="flex items-center">
                            <Clock className="w-3 h-3 mr-1" />
                            {new Date(sample.last_used_at).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short' })}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center space-x-1 ml-2">
                      <button
                        onClick={() => handleEdit(sample)}
                        className="p-1 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded"
                        title="Edit Sample"
                      >
                        <Edit className="w-3 h-3" />
                      </button>
                      <button
                        onClick={() => handleDelete(sample.id)}
                        className="p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded"
                        title="Delete Sample"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Create/Edit Form Modal */}
        {showCreateForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-60 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
              <div className="flex items-center justify-between p-4 border-b">
                <h3 className="text-lg font-semibold">
                  {editingSample ? 'Edit Sample' : 'Create Sample'}
                </h3>
                <button
                  onClick={resetForm}
                  className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="p-4 space-y-4">
                {isImageField ? (
                  <>
                    {/* Kind selector: Text or Image */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Value Type
                      </label>
                      <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden">
                        <button
                          type="button"
                          onClick={() => setSampleKind('text')}
                          className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium transition-colors ${
                            sampleKind === 'text'
                              ? 'bg-blue-600 text-white'
                              : 'bg-white text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          <TypeIcon className="w-4 h-4" />
                          Text
                        </button>
                        <button
                          type="button"
                          onClick={() => setSampleKind('image')}
                          className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-l border-gray-300 transition-colors ${
                            sampleKind === 'image'
                              ? 'bg-blue-600 text-white'
                              : 'bg-white text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          <ImageIcon className="w-4 h-4" />
                          Image
                        </button>
                      </div>
                    </div>

                    {sampleKind === 'text' ? (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Value *
                        </label>
                        <input
                          type="text"
                          value={formData.sample_value}
                          onChange={(e) => setFormData(prev => ({ ...prev, sample_value: e.target.value }))}
                          required
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Enter the value..."
                        />
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {/* Paste URL */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Image URL *
                          </label>
                          <div className="relative">
                            <LinkIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                            <input
                              type="text"
                              value={formData.sample_value}
                              onChange={(e) => setFormData(prev => ({ ...prev, sample_value: e.target.value }))}
                              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              placeholder="Paste an image URL..."
                            />
                          </div>
                        </div>

                        {/* OR upload */}
                        <div className="flex items-center gap-3">
                          <div className="flex-1 h-px bg-gray-200" />
                          <span className="text-xs text-gray-400">OR</span>
                          <div className="flex-1 h-px bg-gray-200" />
                        </div>

                        <div>
                          <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(e) => handleImageUpload(e.target.files?.[0])}
                          />
                          <button
                            type="button"
                            onClick={() => fileInputRef.current?.click()}
                            disabled={uploadingImage}
                            className="w-full flex items-center justify-center gap-2 px-4 py-2 border border-dashed border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {uploadingImage ? (
                              <>
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500" />
                                <span>Uploading...</span>
                              </>
                            ) : (
                              <>
                                <Upload className="w-4 h-4" />
                                <span>Upload image</span>
                              </>
                            )}
                          </button>
                        </div>

                        {/* Preview */}
                        {formData.sample_value && (
                          <div className="mt-2">
                            <div className="text-xs text-gray-500 mb-1">Preview</div>
                            <img
                              src={formData.sample_value}
                              alt="Sample preview"
                              className="max-h-40 rounded-lg border border-gray-200 object-contain"
                              onError={(e) => { e.currentTarget.style.display = 'none'; }}
                            />
                          </div>
                        )}
                      </div>
                    )}

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Language
                      </label>
                      <select
                        value={formData.language}
                        onChange={(e) => setFormData(prev => ({ ...prev, language: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        {availableLanguages.map(lang => (
                          <option key={lang.code} value={lang.code}>{lang.name}</option>
                        ))}
                      </select>
                    </div>
                  </>
                ) : (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Sample Value *
                      </label>
                      <textarea
                        value={formData.sample_value}
                        onChange={(e) => setFormData(prev => ({ ...prev, sample_value: e.target.value }))}
                        required
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Enter the sample value..."
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Type
                        </label>
                        <select
                          value={formData.sample_type}
                          onChange={(e) => setFormData(prev => ({ ...prev, sample_type: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="text">Text</option>
                          <option value="number">Number</option>
                          <option value="date">Date</option>
                          <option value="email">Email</option>
                          <option value="url">URL</option>
                          <option value="phone">Phone</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Language
                        </label>
                        <select
                          value={formData.language}
                          onChange={(e) => setFormData(prev => ({ ...prev, language: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          {availableLanguages.map(lang => (
                            <option key={lang.code} value={lang.code}>{lang.name}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Category
                  </label>
                  <input
                    type="text"
                    value={formData.category}
                    onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Optional category for organization"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tags
                  </label>
                  <input
                    type="text"
                    value={formData.tags}
                    onChange={(e) => setFormData(prev => ({ ...prev, tags: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Comma-separated tags"
                  />
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={resetForm}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading || !formData.sample_value.trim()}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                  >
                    {loading ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    ) : (
                      <Save className="w-4 h-4" />
                    )}
                    <span>{editingSample ? 'Update' : 'Create'} Sample</span>
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GlobalFieldSamplesModal;
